<template>
  <div>
    <div class="employee-actions">
        <template v-if="!isArchived && !item.external">
          <!-- Icons for the roles this employee actually has (built-in + custom) -->
          <v-tooltip v-for="role in assignedRoles" :key="role.key" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-badge
                overlap
                :value="!!getRoleStatusIcon(role)"
                class="mr-2 small-badge"
              >
                <template v-slot:badge>
                  <v-icon small :color="getRoleStatusColor(role)">
                    {{ getRoleStatusIcon(role) }}
                  </v-icon>
                </template>

                <v-icon
                  medium
                  :class="{ 'employee-role--readonly': isReadOnlyUser }"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="!isReadOnlyUser && openUploadDialog(role)"
                  :data-cy="role.key"
                >
                  {{ role.icon }}
                </v-icon>
              </v-badge>
            </template>

            <span>
              {{ role.label }}
              <template v-if="isRoleOverdue(role)">
                – {{ $t("employee.roleOverdue") }}</template
              >
            </span>
          </v-tooltip>

          <!-- Assign-role button -->
          <v-tooltip bottom v-if="!isReadOnlyUser">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                medium
                class="mr-2"
                v-bind="attrs"
                v-on="on"
                @click.stop="openAddRoleDialog"
                data-cy="assign-role"
              >
                mdi-account-plus-outline
              </v-icon>
            </template>
            <span>{{ $t("employee.assignRole") }}</span>
          </v-tooltip>
        </template>

        <v-tooltip bottom v-if="!item.external && !isReadOnlyUser">
          <template v-slot:activator="{ on }">
            <v-icon
              medium
              class="mr-2"
              v-on="on"
              @click.stop="openArchiveDialog(item)"
              :data-cy="isArchived ? 'unarchive' : 'archive'"
            >
              {{ isArchived ? "unarchive" : "archive" }}
            </v-icon>
          </template>
          <span>{{
            isArchived
              ? $t("employee.unarchiveEmployee")
              : $t("employee.archiveEmployee")
          }}</span>
        </v-tooltip>
        <v-tooltip bottom v-if="!isReadOnlyUser">
          <template v-slot:activator="{ on }">
            <v-icon
              medium
              class="mr-2"
              v-on="on"
              @click.stop="editItem(item)"
              data-cy="edit"
            >
              edit
            </v-icon>
          </template>
          <span>{{ $t("employee.editEmployee") }}</span>
        </v-tooltip>
        <v-tooltip bottom v-if="canDeleteHere">
          <template v-slot:activator="{ on }">
            <v-icon
              medium
              class="mr-2"
              v-on="on"
              @click.stop="openDeleteDialog(item)"
              data-cy="delete"
            >
              delete</v-icon
            >
          </template>
          <span>{{ $t("employee.deleteEmployee") }}</span>
        </v-tooltip>
    </div>
    <deleteDialog
      v-on:triggerDelete="deleteItem"
      v-bind:item="item"
      v-bind:deleteDialog="deleteDialog"
    />
    <archiveDialog
      v-on:triggerToggleArchive="toggleArchiveItem"
      v-bind:item="item"
      v-bind:archiveDialog="archiveDialog"
    />
    <addRoleDialog
      v-model="addRoleDialog.open"
      :item="item"
      :assignedKeys="assignedKeys"
      :builtInRoles="builtInRoles"
      @select="onSelectRole"
    />
    <uploadDialog
      :dialog="uploadDialog"
      :item="item"
      @triggerSaveUpload="saveRoleDialog"
      @triggerDeleteRole="deleteRole"
    />
  </div>
</template>

<script>
import archiveDialog from "../../_utils/dataTableHelpers/actions/archiveDialog.vue";
import deleteDialog from "../../_utils/dataTableHelpers/actions/deleteDialog.vue";
import uploadDialog from "../../_utils/dataTableHelpers/actions/uploadDialog.vue";
import addRoleDialog from "../../_utils/dataTableHelpers/actions/addRoleDialog.vue";

const BUILT_IN_ROLES = [
  {
    key: "safetyOfficer",
    icon: "mdi-shield-account",
    label: "employee.safetyOfficer",
    requireDate: false,
    requireFile: true,
    showDate: false,
  },
  {
    key: "fireMarshal",
    icon: "mdi-fire",
    label: "employee.fireMarshal",
    requireDate: true,
    requireFile: true,
    showDate: true,
  },
  {
    key: "firstAider",
    icon: "local_hospital",
    label: "employee.firstAider",
    requireDate: true,
    requireFile: true,
    showDate: true,
  },
];

export default {
  props: ["item"],
  components: {
    deleteDialog,
    archiveDialog,
    uploadDialog,
    addRoleDialog,
  },
  data: () => ({
    deleteDialog: {
      open: false,
      title: "employee.deleteTitle",
      description: "",
    },
    archiveDialog: {
      open: false,
      title: "employee.archiveTitle",
      description: "",
      mode: "archive",
      button: "",
    },
    uploadDialog: {
      open: false,
      role: null,
      roleLabel: "",
      requireDate: false,
      requireFile: false,
      showDate: true,
    },
    addRoleDialog: {
      open: false,
    },
  }),
  computed: {
    isArchived() {
      return !!this.item?.archived;
    },
    builtInRoles() {
      return BUILT_IN_ROLES.map((r) => ({
        ...r,
        label: this.$t(r.label),
        isBuiltIn: true,
      }));
    },
    customRoleDefs() {
      return this.$store.getters.employeeRoleDefinitions || [];
    },
    assignedKeys() {
      return (this.item?.roles || []).map((r) => r.role);
    },
    assignedRoles() {
      return (this.item?.roles || [])
        .map((r) => this.resolveRole(r.role))
        .filter(Boolean);
    },
  },
  methods: {
    resolveRole(key) {
      const builtIn = this.builtInRoles.find((r) => r.key === key);
      if (builtIn) return builtIn;

      const def = this.customRoleDefs.find((d) => d._id === key);
      if (def) {
        return {
          key: def._id,
          icon: def.icon || "mdi-account-star",
          label: def.name,
          requireDate: false,
          requireFile: false,
          showDate: true,
          isBuiltIn: false,
        };
      }

      return null;
    },
    editItem(item) {
      this.$router.push({
        name: "employeeForm",
        params: { id: item._id, mode: "edit" },
      });
    },
    openDeleteDialog(item) {
      this.deleteDialog.open = true;
      this.deleteDialog.description =
        this.$t("employee.deleteConfirm", { name: item.name });
    },
    openArchiveDialog(item) {
      const archived = !!item.archived;

      this.archiveDialog.mode = archived ? "unarchive" : "archive";
      this.archiveDialog.open = true;

      if (archived) {
        this.archiveDialog.title = "employee.unarchiveTitle";
        this.archiveDialog.description =
          this.$t("employee.unarchiveConfirm", { name: item.name });
        this.archiveDialog.button = "general.unarchive";
      } else {
        this.archiveDialog.title = "employee.archiveTitle";
        this.archiveDialog.description =
          this.$t("employee.archiveConfirm", { name: item.name });
        this.archiveDialog.button = "general.archive";
      }
    },
    deleteItem(item) {
      this.$store.dispatch("deleteEmployee", item).then(() => {
        this.reloadDataFunc();
        this.$root.$emit("employee-stats-refresh");
        this.deleteDialog.open = false;
      });
    },
    toggleArchiveItem(item) {
      this.$store
        .dispatch("archiveEmployee", {
          _id: item._id,
          archived: this.archiveDialog.mode === "archive" ? true : false,
        })
        .then(() => {
          this.reloadDataFunc();
          this.$root.$emit("employee-stats-refresh");
          this.archiveDialog.open = false;
        });
    },
    reloadDataFunc() {
      this.$emit("reloadData");
    },
    openAddRoleDialog() {
      this.addRoleDialog.open = true;
    },
    onSelectRole(role) {
      this.addRoleDialog.open = false;
      this.openUploadDialog(role);
    },
    openUploadDialog(role) {
      this.uploadDialog.role = role.key;
      this.uploadDialog.roleLabel = role.label;
      this.uploadDialog.requireDate = !!role.requireDate;
      this.uploadDialog.requireFile = !!role.requireFile;
      this.uploadDialog.showDate = role.showDate !== false;
      this.uploadDialog.open = true;
    },
    async deleteRole({ _id, role }) {
      await this.$store.dispatch("deleteEmployeeRole", { _id, role });
      this.reloadDataFunc();
      this.$root.$emit("employee-stats-refresh");
    },
    async saveRoleDialog(payload) {
      this.$store
        .dispatch("updateEmployeeRole", payload)
        .then((updatedEmployee) => {
          if (updatedEmployee && updatedEmployee.roles) {
            this.$set(this.item, "roles", updatedEmployee.roles);
          }

          this.$root.$emit("employee-stats-refresh");

          this.reloadDataFunc();

          this.uploadDialog.open = false;
        });
    },
    statusForRole(role) {
      const entry = (this.item?.roles || []).find((r) => r.role === role.key);
      if (!entry) return null;

      const hasDate = !!entry.nextDate;

      if (!hasDate) {
        // Built-in role that needs a date but has none -> missing proof.
        if (role.requireDate) return "expired";
        return role.isBuiltIn ? "done" : null;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const nextDate = new Date(entry.nextDate);
      nextDate.setHours(0, 0, 0, 0);

      // A due date in the past means the role/certification is overdue.
      return nextDate >= today ? "done" : "overdue";
    },
    isRoleOverdue(role) {
      return this.statusForRole(role) === "overdue";
    },
    getRoleStatusIcon(role) {
      const status = this.statusForRole(role);
      if (status === "done") return "mdi-check-circle";
      if (status === "overdue") return "mdi-clock";
      if (status === "expired") return "mdi-close-circle";
      return null;
    },
    getRoleStatusColor(role) {
      const status = this.statusForRole(role);
      if (status === "done") return "success";
      if (status === "overdue") return "error";
      if (status === "expired") return "error";
      return "";
    },
  },
};
</script>

<style>
.employee-role--readonly {
  cursor: default !important;
}
/* One single row of icons (roles, "add", and the management actions all
   treated the same). The row lives in a fixed-width box that is centred in the
   column, and the icons are right-aligned inside it. Result: the right edge
   (edit/delete) sits at the same spot on every row, and role icons grow to the
   left from that fixed anchor instead of hugging the column's right corner. */
.employee-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;
  width: 100%;
  box-sizing: border-box;
  /* Right edge of the icon row is anchored just right of the column centre, so
     the always-present buttons straddle the centre and role icons grow left. */
  padding-right: calc(50% - 80px);
}

.v-badge.small-badge .v-badge__badge {
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 !important;
  min-width: 12px !important;
  height: 12px !important;
  width: 12px !important;
  border-radius: 50% !important;
  box-shadow: none !important;
  inset: auto auto calc(100% - 8px) calc(100% - 8px) !important;
  background-color: #1e1e1e !important;
}
</style>
